import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

export default function WhatsAppAdminPage() {
  const [settings, setSettings] = useState(null);
  const [testNum, setTestNum] = useState("");

  const load = () => api.get("/settings").then((r) => setSettings(r.data));
  useEffect(() => { load(); }, []);
  if (!settings) return <div className="text-muted-foreground">Loading…</div>;
  const wa = settings.whatsapp || {};
  const set = (patch) => setSettings((s) => ({ ...s, whatsapp: { ...s.whatsapp, ...patch } }));

  const save = async () => {
    const phoneNumberId = String(
      settings.whatsapp?.phone_number_id || ""
    ).trim();

    const accessToken = String(
      settings.whatsapp?.access_token || ""
    ).trim();

    if (phoneNumberId && !/^\d+$/.test(phoneNumberId)) {
      return toast.error("Phone Number ID must contain digits only");
    }

    if (settings.whatsapp?.enabled && !phoneNumberId) {
      return toast.error("Phone Number ID is required");
    }

    // A blank token keeps the saved one.
    if (settings.whatsapp?.enabled && !accessToken && !settings.whatsapp?.access_token_set) {
      return toast.error("Access Token is required");
    }

    try {
      const { data } = await api.put(
        "/settings/whatsapp",
        settings.whatsapp || {}
      );
      setSettings(data);
      toast.success("WhatsApp settings saved");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const test = async () => {
    if (!testNum) return toast.error("Provide a phone number");
    try {
      const { data } = await api.post("/settings/test-whatsapp", { to: testNum });
      if (data.status === "sent") toast.success("Test WhatsApp message sent");
      else toast.error(`WhatsApp test ${data.status}: ${data.error || "unknown error"}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="admin-whatsapp-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">System</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">WhatsApp (Meta Cloud API)</h1>
        <p className="text-sm text-muted-foreground">Configure WhatsApp notifications through Meta Cloud API. In test mode messages are logged only, not sent.</p>
      </div>

      <div className="rounded-xl border border-border bg-card p-5 space-y-4">
        <div className="flex items-center justify-between">
          <Label>Enable WhatsApp delivery</Label>
          <Switch data-testid="wa-enabled" checked={!!wa.enabled} onCheckedChange={(v) => set({ enabled: v })} />
        </div>
        <div className="flex items-center justify-between">
          <Label>Test mode (log only, no live sends)</Label>
          <Switch data-testid="wa-test-mode" checked={!!wa.test_mode} onCheckedChange={(v) => set({ test_mode: v })} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label>Provider</Label><Input value={wa.provider || "meta_cloud"} disabled /></div>
          <div><Label>Phone Number ID</Label><Input data-testid="wa-phone-id" value={wa.phone_number_id || ""} onChange={(e) => set({ phone_number_id: e.target.value })} /></div>
          <div className="md:col-span-2"><Label>Access Token</Label><Input data-testid="wa-token" type="password" value={wa.access_token || ""} placeholder={wa.access_token_set ? "Saved — leave blank to keep" : ""} onChange={(e) => set({ access_token: e.target.value })} /></div>
          <div><Label>Default country code</Label><Input data-testid="wa-country-code" value={wa.default_country_code ?? "91"} onChange={(e) => set({ default_country_code: e.target.value })} placeholder="91" /></div>
          <div><Label>Template language</Label><Input data-testid="wa-template-language" value={wa.template_language ?? "en"} onChange={(e) => set({ template_language: e.target.value })} placeholder="en" /></div>
          <div className="md:col-span-2">
            <Label>Approved template name</Label>
            <Input data-testid="wa-template" value={wa.default_template || ""} onChange={(e) => set({ default_template: e.target.value })} />
            <p className="mt-1 text-[11px] text-muted-foreground">
              Meta only delivers business-initiated messages through an approved template. Use a template whose body has one variable {"{{1}}"}; the notification text fills it. Leave blank to send plain text, which Meta delivers only if the user messaged you in the last 24 hours.
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button data-testid="wa-save" onClick={save} className="bg-primary text-primary-foreground hover:bg-primary/90">Save</Button>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-5 space-y-3">
        <Label>Send test WhatsApp</Label>
        <div className="flex gap-2">
          <Input data-testid="wa-test-to" value={testNum} onChange={(e) => setTestNum(e.target.value)} placeholder="+919876543210" />
          <Button data-testid="wa-test" variant="outline" onClick={test}>Send test</Button>
        </div>
      </div>
    </div>
  );
}
